import React, { useState, useEffect, useRef } from 'react';
import PropTypes from "prop-types";
import './scrollable-text-area.css';
import CustomScroll from 'react-custom-scroll';
import './customScroll.css';
import RenderHtmlElements from 'react-render-html';
import { addKeydownEvent, removeKeydownEvent } from '../utils/keydown-event-handler';

/**
 * The ScrollableTextArea is a component that present a very long text in a small container so that you can scroll up and down to read the text above. The _text_ property can render also HTML tags.
 * #### __Note__: 
 * it's important to set an __height__ in px in _containerClassname_ property to correctly have the scroll effect.
*/
const AntaresScrollableTextArea = (props) => {
    const [scroll, setScroll] = useState(0);
    const scrollRef = useRef(0);
    const bottomReached = useRef(false);
    const topReached = useRef(true);
    const [text, setText] = useState(null);
    const keyDownEvent = useRef(null);
    const [active, setActive] = useState(true);


    useEffect(() => {
        keyDownEvent.current = addKeydownEvent(onKeyDown);
        return () => {
            removeKeydownEvent(keyDownEvent.current);
        }
    }, []);

    useEffect(() => {
        if (text !== props.text) {
            setText(props.text);
            setScroll(0);
            topReached.current = true;
            bottomReached.current = false;
        }
    }, [props.text]);

    useEffect(() => {
        if (props.active !== active) {
            setActive(props.active);
        }
    }, [props.active]);

    const onKeyDown = (e) => {
        if (!active) return;
        if (e.keyCode === 38) { //up
            e.preventDefault();
            if (!topReached.current)
                scrollList('up');
        } else if (e.keyCode === 40) {
            e.preventDefault();
            if (!bottomReached.current)
                scrollList('down');
        }
        return false;
    }

    const scrollList = (lastDirection) => {
        const lineHeightRatio = props.lineHeight * props.scrollRatioFactor;
        let direction = null;

        if (lastDirection !== null) {
            if (lastDirection === 'up'){
                direction = -1
            }     
            else {
                direction = 1
            }
                
            if(direction !== null) {
                scrollRef.current = (scrollRef.current  + (lineHeightRatio * direction) <= 0 ? 0 : scrollRef.current + (lineHeightRatio * direction));
                setScroll(scrollRef.current);
            }   
        } 
    }

    const onScroll = (e) => {
        const element = e.currentTarget;
        if (element.scrollTop <= 0) {
            topReached.current = true;
            bottomReached.current = false;
        } else if ((element.scrollHeight - Math.round(element.scrollTop)) === element.clientHeight) {  
            topReached.current = false;
            bottomReached.current = true;
        } else {
            topReached.current = false;
            bottomReached.current = false;
        }
        if (props.onScroll) {
            props.onScroll(topReached.current, bottomReached.current)
        }
    }

    return (
        <div className={`antares-scrollable-text-area-container ${props.containerClassname}`}>
            <div className='antares-scrollable-text-area-wrapper'>
                <CustomScroll
                    heightRelativeToParent={`calc(100% - ${props.heightRelativeToParent}px)`}
                    scrollTo={scroll}
                    minScrollHandleHeight={props.minScrollHandleHeight}
                    onScroll={onScroll}>
                    <div className={`antares-scrollable-text-area ${props.classnameTextArea}`} id='antares-scrollable-text-area'>
                        <span className={`antares-text-area-text ${props.textClassName}`} id='antares-scrollable-text-area-text'>{RenderHtmlElements(props.text)}</span>
                    </div>
                </CustomScroll>
            </div>
        </div>
    )
}

AntaresScrollableTextArea.propTypes = {
    /**
    *  this function has been called everytime a scroll occurrs. It receives __topReached__ and __bottomReached__ parameters that allows user to know if top/bottom has been reached.
    */
    onScroll: PropTypes.func,
    /**
   *  style of textArea container.
   */
    containerClassname: PropTypes.string,
    /**
    *  textArea style.
    */
    textAreaClassname: PropTypes.string,
    /**
    *  text to render.
    */
    text: PropTypes.string,
    /**
    *  text style.
    */
    textClassName: PropTypes.string,
    /**
    *  if the text you want to render is too long and the container height is small, the track in the scrollbar may appear too small. This property allows you to set a minimum track height.
    */
    minScrollHandleHeight: PropTypes.number,
    /**
    *  this property allows you to set a relative textArea height relative to the parent.
    */
    heightRelativeToParent: PropTypes.number,
    /**
   *  this property allows you to set a scroll ratio factor.
   */
    scrollRatioFactor: PropTypes.number,
    /**
   *  this property allows you to set the lineHeight value, so that user can scroll of this value.
   */
    lineHeight: PropTypes.number,
    /**
  *  this property allows you to set if textarea is active or not.
  */
    active: PropTypes.bool
}

AntaresScrollableTextArea.defaultProps = {
    onScroll: () => { },
    text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur non enim a diam maximus condimentum. Praesent venenatis, sapien vitae ullamcorper tempor, nisi elit hendrerit dolor, quis convallis lacus odio posuere elit. Suspendisse a fermentum mi. Sed sed mi sed arcu ullamcorper tempus et sed augue. Integer et augue sollicitudin, lacinia risus in, mattis neque. Duis lobortis scelerisque semper. Pellentesque mattis ex at auctor sodales. Nunc posuere feugiat quam. Aenean id felis pharetra, faucibus erat interdum, rutrum magna. Nunc laoreet elementum tellus, eget faucibus nisl rutrum venenatis. Phasellus dictum gravida neque a placerat. Cras porttitor, dui id venenatis vulputate, odio risus venenatis ligula, non aliquam urna libero id sem. Sed consectetur velit quam, in finibus tortor dapibus sed. Proin eu placerat ipsum. Ut vel facilisis libero. Morbi venenatis, ex sit amet molestie molestie, lorem urna aliquet metus, quis volutpat nibh nisi a lorem. Nam convallis in odio vitae aliquet. Mauris egestas diam hendrerit tortor fringilla molestie. Duis vel tincidunt elit. Maecenas sed libero sem. Nullam consectetur ornare justo, eget sodales mauris egestas sed. Quisque rhoncus vitae justo vel rutrum. Mauris vulputate aliquam luctus. Integer pharetra ligula sit amet consectetur porta. Vestibulum fermentum vel velit vel luctus. Pellentesque tincidunt consequat lacus, quis consectetur sapien. Proin ultrices vehicula elit et scelerisque. Cras porttitor, dui id venenatis vulputate, odio risus venenatis ligula, non aliquam urna libero id sem. Sed consectetur velit quam, in finibus tortor dapibus sed. Proin eu placerat ipsum. Ut vel facilisis libero. Morbi venenatis, ex sit amet molestie molestie, lorem urna aliquet metus, quis volutpat nibh nisi a lorem. Nam convallis in odio vitae aliquet. Mauris egestas diam hendrerit tortor fringilla molestie. Duis vel tincidunt elit. Maecenas sed libero sem. Nullam consectetur ornare justo, eget sodales mauris egestas sed. Quisque rhoncus vitae justo vel rutrum. Mauris vulputate aliquam luctus. Integer pharetra ligula sit amet consectetur porta. Vestibulum fermentum vel velit vel luctus. Pellentesque tincidunt consequat lacus, quis consectetur sapien. Proin ultrices vehicula elit et scelerisque. ##### Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur non enim a diam maximus condimentum. Praesent venenatis, sapien vitae ullamcorper tempor, nisi elit hendrerit dolor, quis convallis lacus odio posuere elit. Suspendisse a fermentum mi. Sed sed mi sed arcu ullamcorper tempus et sed augue. Integer et augue sollicitudin, lacinia risus in, mattis neque. Duis lobortis scelerisque semper. Pellentesque mattis ex at auctor sodales. Nunc posuere feugiat quam. Aenean id felis pharetra, faucibus erat interdum, rutrum magna. ######### Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur non enim a diam maximus condimentum. Praesent venenatis, sapien vitae ullamcorper tempor, nisi elit hendrerit dolor, quis convallis lacus odio posuere elit. Suspendisse a fermentum mi. Sed sed mi sed arcu ullamcorper tempus et sed augue. Integer et augue sollicitudin, lacinia risus in, mattis neque. Duis lobortis scelerisque semper. Pellentesque mattis ex at auctor sodales.',
    minScrollHandleHeight: 10,
    heightRelativeToParent: 0,
    scrollRatioFactor: 1.618,
    lineHeight: 40,
    active: true
}

export default AntaresScrollableTextArea;